import { Component, input, signal, computed, ElementRef, viewChildren, output, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { OrderProcessingService } from '../../../core/services/order-processing';
import { OrderSelectionService } from '../../../core/services/order-selection.service';

@Component({
    selector: 'app-upload-box',
    imports: [],
    templateUrl: './upload-box.html',
    styleUrl: './upload-box.scss',
})
export class UploadBox {
    docCount = input<number>(1);
    customHeadings = input<string[]>([]);
    featuredImage = input<string>('');

    currentIndex = signal(0);
    uploadedFiles = signal<(File | null)[]>([]);

    /** Modal state */
    showPreviewModal = signal(false);
    previewUrl = signal<SafeResourceUrl | null>(null);
    private pendingFile: File | null = null;
    private pendingIndex: number = 0;
    private pendingObjectUrl: string | null = null;

    /** Reference to all file inputs */
    fileInputs = viewChildren<ElementRef<HTMLInputElement>>('fileInput');

    uploadsCompleted = output<number[]>();
    proceedWithHardCopy = output<void>();

    isUploading = signal(false);
    uploadError = signal<string | null>(null);
    uploadedIds = signal<any[]>([]);

    showExtraPagesModal = signal(false);
    pendingPageCount = signal(0);

    orderSelection = inject(OrderSelectionService);
    private orderProcessing = inject(OrderProcessingService);

    constructor(private sanitizer: DomSanitizer) { }

    /** Whether all documents have been uploaded */
    allDone = computed(() => this.currentIndex() >= this.docCount());

    /** The heading text for the current upload step */
    heading = computed(() => {
        if (this.allDone()) {
            return 'All documents uploaded!';
        }
        const custom = this.customHeadings();
        if (custom && custom.length > this.currentIndex()) {
            return custom[this.currentIndex()];
        }

        const count = this.docCount();
        if (count === 1) {
            return 'Please upload your document';
        }

        return `Please upload your ${this.getOrdinal(this.currentIndex() + 1)} document`;
    });

    /** Array of indices for the template @for loop */
    indices = computed(() => Array.from({ length: this.docCount() }, (_, i) => i));

    onFileSelected(event: Event, index: number) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];
            this.pendingFile = file;
            this.pendingIndex = index;

            // Create object URL for iframe preview
            this.pendingObjectUrl = URL.createObjectURL(file);
            this.previewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.pendingObjectUrl));
            this.showPreviewModal.set(true);
        }
    }

    async onAcceptFile() {
        if (this.pendingFile !== null) {
            try {
                const arrayBuffer = await this.pendingFile.arrayBuffer();
                const { PDFDocument } = await import('pdf-lib');
                const pdfDoc = await PDFDocument.load(arrayBuffer);
                const pageCount = pdfDoc.getPageCount();

                if (pageCount > 10) {
                    this.pendingPageCount.set(pageCount);
                    this.showPreviewModal.set(false);
                    this.showExtraPagesModal.set(true);
                    return;
                }
            } catch (e) {
                console.error("Failed to parse PDF", e);
            }
            this.commitPendingFile(0);
        } else {
            this.closeModal();
        }
    }

    commitPendingFile(extraPages: number) {
        if (this.pendingFile !== null) {
            const current = [...this.uploadedFiles()];
            current[this.pendingIndex] = this.pendingFile;
            this.uploadedFiles.set(current);

            if (extraPages > 0) {
                this.orderSelection.addExtraPages(extraPages);
            }

            const nextIndex = this.pendingIndex + 1;
            this.currentIndex.set(nextIndex);

            if (nextIndex >= this.docCount()) {
                this.processUploads();
            }
        }
        this.closeModal();
    }

    onAcceptExtraPages() {
        const extra = this.pendingPageCount() - 10;
        this.commitPendingFile(extra > 0 ? extra : 0);
    }

    onHardCopyFromUpload() {
        this.closeModal();
        this.proceedWithHardCopy.emit();
    }

    async processUploads() {
        this.isUploading.set(true);
        this.orderProcessing.isUploading.set(true);
        this.uploadError.set(null);
        const files = this.uploadedFiles();
        const currentIds = [...this.uploadedIds()];

        const uploadPromises = files.map(async (file, index) => {
            if (currentIds[index]) {
                return currentIds[index];
            }
            if (file) {
                const res = await this.orderProcessing.uploadFile(file);
                return res?.data?.id || res?.id || res;
            }
            return null;
        });

        try {
            const results = await Promise.allSettled(uploadPromises);
            let hasError = false;
            let firstErrorIndex = -1;
            const errorMessages: string[] = [];
            const newFiles = [...this.uploadedFiles()];
            const newIds = [...this.uploadedIds()];
            let theError = ""
            results.forEach((result, idx) => {
                if (result.status === 'rejected') {
                    hasError = true;
                    theError = result.reason.error.message
                    const failedFile = files[idx];
                    if (failedFile) errorMessages.push(failedFile.name);
                    newFiles[idx] = null;
                    if (firstErrorIndex === -1) {
                        firstErrorIndex = idx;
                    }
                    const inputs = this.fileInputs();
                    if (inputs[idx]) {
                        inputs[idx].nativeElement.value = '';
                    }
                } else {
                    newIds[idx] = result.value;
                }
            });

            this.uploadedIds.set(newIds);
            this.uploadedFiles.set(newFiles);
            if (hasError) {
                this.uploadError.set(`Failed to upload: ${errorMessages.join(', ')}. ${theError}`);
                this.currentIndex.set(firstErrorIndex);
            } else {
                this.uploadsCompleted.emit(newIds);
            }
        } finally {
            this.isUploading.set(false);
            this.orderProcessing.isUploading.set(false);
        }
    }

    onCancelFile() {
        // Clear the file input value so the user can re-select
        const inputs = this.fileInputs();
        if (inputs[this.pendingIndex]) {
            inputs[this.pendingIndex].nativeElement.value = '';
        }
        this.closeModal();
    }

    private closeModal() {
        this.showPreviewModal.set(false);
        this.showExtraPagesModal.set(false);
        if (this.pendingObjectUrl) {
            URL.revokeObjectURL(this.pendingObjectUrl);
            this.pendingObjectUrl = null;
        }
        this.previewUrl.set(null);
        this.pendingFile = null;
        this.pendingPageCount.set(0);
    }

    private getOrdinal(n: number): string {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }
}
